<script setup lang="ts">
/**
 * UI-kit component
 * Category: Tooltip
 * Name: 'Tooltip'
 * FIGMA: 'https://www.figma.com/design/CFyOwQUjj7uVRQTc03muvG/CS-UI-kit?node-id=6261-10132&m=dev'
 */
import type { AppTooltipProps } from '@UI/Tooltips/AppTooltip/types/interfaces'

import { useAppTooltip } from '@UI/Tooltips/AppTooltip/composables/use-app-tooltip.hook'

defineOptions({
  name: 'AppTooltip',
})

const props = withDefaults(defineProps<AppTooltipProps>(), {
  placement: 'bottom',
  size: 'default',
  mode: 'hover',
  offset: 4,
  delay: 100,
  teleportTo: 'body',
})

const {
  tooltipId,
  triggerRef,
  tooltipRef,
  show,
  tooltipState,
  floatingStyles,
  resolvedPlacement,
  resolvedSide,
  teleportTarget,
  showTooltip,
  hideTooltip,
  toggleTooltip,
  handleAnimationEnd,
  handleKeydown,
} = useAppTooltip(props)
</script>

<template>
  <span
    ref="triggerRef"
    class="app-tooltip-trigger"
    :aria-describedby="show ? tooltipId : undefined"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @touchstart.passive="toggleTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
    @keydown="handleKeydown"
  >
    <slot />
  </span>

  <Teleport
    :to="teleportTarget"
    :disabled="teleportTo === false"
  >
    <div
      ref="tooltipRef"
      class="app-tooltip"
      :style="floatingStyles"
    >
      <div
        v-if="show"
        :id="tooltipId"
        role="tooltip"
        :class="[
          'app-tooltip__body',
          `app-tooltip__body--${props.size}`,
        ]"
        :data-state="tooltipState"
        :data-placement="resolvedPlacement"
        :data-side="resolvedSide"
        @animationend="handleAnimationEnd"
      >
        <span class="app-tooltip__content">
          {{ props.text }}
        </span>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped src="../styles/_app-tooltip.scss" />
